import React, { useImperativeHandle, useState } from "react";
import style from "./index.module.scss";
import { Icon, Input, InputNumber, message, Modal, Tooltip } from "antd";
import { ModalProps } from "antd/es/modal";
import { ResultMessage } from "components/ResultMessage";
import { errorMessage } from "service/service";
import useRequestFn from "utils/useRequestFn";
import { saveGrade } from "service/score";
import Form, { useForm } from "rc-field-form";
import FieldWithError from "components/FieldWithError";

interface SettingModalProps extends ModalProps {
    onCancel: () => void;
    onSuccess: () => void;
}

interface DataModalInstance {
    setPayload: (payload: any) => void;
}

interface FormProp {
    finalGrade: string;
    remark: string;
}

const { TextArea } = Input;

const EditModal = React.forwardRef<DataModalInstance, SettingModalProps>(
    ({ onSuccess, onCancel, ...props }, ref) => {
        const [form] = useForm<FormProp>();

        const [payload, setPayload] =
            useState<Record<string, number | string | undefined>>();

        useImperativeHandle(ref, () => ({
            setPayload: payload => {
                setPayload(payload);
            }
        }));

        const [updateAction, { loading }] = useRequestFn(saveGrade);

        async function onFinish(data: FormProp) {
            if (payload) {
                const params = {
                    courseNo: payload.courseNo,
                    courseSeq: payload.courseSeq,
                    studentNo: payload.studentNo,
                    studentName: payload.studentName,
                    finalGrade: data.finalGrade,
                    remark: data.remark
                };
                try {
                    await updateAction(params);
                    await onSuccess();
                    onCancel();
                    message.success("修改成功");
                } catch (e) {
                    ResultMessage.error(errorMessage(e));
                }
            }
        }

        const tooltipTitle = (
            <div className={style.toolTip}>
                <div className={style.tTitle}>补考成绩示例</div>
                <div className={style.tContent}>
                    课程考试满分100分，60分及格，其中平时成绩占40%，期末成绩60%。
                    <br />
                    学生平时成绩为20分（折算后），期末成绩为35分（折算后），总成绩55分，不及格参加补考，补考卷面成绩分90分。
                    <br />
                    <br />
                    计算最终成绩=平时成绩+折算后补考成绩=20+90*60%=74分。
                    <br />
                    将74分录入最终成绩，并在说明中记录：学生参加补考，卷面成绩为90分。
                    <br />
                </div>
            </div>
        );

        return (
            <Modal
                width={610}
                title="修改成绩"
                maskClosable={false}
                className={style.root}
                onCancel={onCancel}
                onOk={form.submit}
                confirmLoading={loading}
                {...props}>
                <Form form={form} onFinish={onFinish}>
                    <div className={style.tips}>
                        说明：
                        <br />
                        1、修改后成绩为最终成绩，可能出现各项成绩相加不等于总成绩的情况，请谨慎修改。
                        <br />
                        2、补考、缓考成绩也通过最终成绩调整。任课教师计算最终学生得分，并填写修改说明，最终成绩=平时成绩+补考卷面成绩*期末考试权重。
                    </div>
                    <div className={style.info}>
                        {payload?.studentName}&nbsp;&nbsp; {payload?.studentNo}
                    </div>
                    <div className={style.label}>
                        <span>
                            原成绩：
                            <b className={style.score}>{payload?.totalGrade}</b>
                        </span>
                        <span>
                            最终成绩：
                            <Tooltip placement="top" title={tooltipTitle}>
                                <Icon type="question-circle" />
                            </Tooltip>
                            &nbsp;&nbsp;
                            <Form.Field
                                name="finalGrade"
                                initialValue={payload?.finalGrade}>
                                <InputNumber
                                    min={0}
                                    max={100}
                                    precision={1}
                                    placeholder="请输入"
                                    className={style.inputNumber}
                                />
                            </Form.Field>
                        </span>
                    </div>
                    <div className={style.label}>
                        <i className={style.required}>*</i>修改原因：
                    </div>
                    <FieldWithError
                        name="remark"
                        initialValue={payload?.remark}
                        rules={[
                            {
                                required: true,
                                message: "修改原因必填，请填写后再提交保存"
                            }
                        ]}>
                        <TextArea
                            maxLength={50}
                            placeholder="请说明修改成绩原因"
                            autoSize={{ minRows: 4, maxRows: 6 }}
                        />
                    </FieldWithError>
                </Form>
            </Modal>
        );
    }
);
EditModal.displayName = "EditModal";

export default EditModal;
